import { PageShell, GetStarted } from '../components';
import {
  PageBody,
  PageHero,
  Section,
  FeatureRows,
  Steps,
  DataTable,
  Note,
  SmartLink,
  PaintingWindow,
} from '../components/page';
import { LiveGenerateWindow } from '../components/product/live';

/**
 * MCP generation: OpenAPI spec in, governed MCP server out. Every claim here
 * was checked against services/mcp-generator and pkg/registry in Pugio
 * (inventory of 2026-10-04). Mode names are the product's: Per Endpoint,
 * Generic, Curated.
 *
 * Deliberately not claimed: Swagger 2.0 or OAuth2 support, spec file upload in
 * the app (URL only), Curated mode in the app (CLI only), stdio or SSE
 * transports, tool annotations, pagination, generated tests, automatic spec
 * polling, a published CLI package, or any measured token count (every token
 * figure is an estimate and is labelled so).
 */

const HOW = [
  {
    title: 'Give Statio your spec',
    body: 'Paste the URL of an OpenAPI 3 spec. Statio fetches it, counts the endpoints and shows the tools it will generate, along with the base URL those tools will call, which you can change.',
  },
  {
    title: 'Pick a generation mode',
    body: 'One tool per endpoint for small APIs, or three generic tools for large ones. The modes are described below.',
  },
  {
    title: 'Ship a version',
    body: 'Statio generates the server, builds the image, deploys it privately on Google Cloud Run, discovers its tools and moves traffic to it. You can follow each stage as it runs.',
  },
  {
    title: 'Grant it, tool by tool',
    body: 'Every generated tool becomes something you can grant or deny per member and per agent, like any other server behind the gateway.',
  },
];

const MODES = [
  {
    meta: 'Per Endpoint',
    title: 'One tool per endpoint',
    body: (
      <>
        <p>
          Every operation in the spec becomes its own tool, named from its operationId, with its path and query
          parameters typed from the spec. Best for small, focused APIs where you want the model to see the whole surface
          up front.
        </p>
        <p>Filter by tag, path, method or operation to keep only the endpoints you want.</p>
      </>
    ),
  },
  {
    meta: 'Generic',
    title: 'Three tools, whatever the size of the API',
    body: (
      <>
        <p>
          The server registers exactly three tools, whether the API has 20 endpoints or 1,000. The model browses, reads
          the schema it needs, then calls. Everything else stays on the server in an endpoint map and is only sent when
          asked for.
        </p>
        <p>
          The list of tools the model reads on connect stays the same size as the API grows: an estimated 800 tokens.
        </p>
      </>
    ),
  },
  {
    meta: 'Curated',
    title: 'Hand-picked tools, with a fallback',
    body: (
      <>
        <p>
          Name the operations that matter as direct tools, with your own names and descriptions. A fallback tool still
          reaches every other endpoint, and an explorer and schema tool help the model find them. Curated mode is
          configured in mcp-gen.yaml with the CLI today.
        </p>
        <code className="pg-cmd">{`generation:
  mode: curated
  curated:
    tools:
      - operationId: createCustomer
      - operationId: getInvoice
        name: stripe_invoice
      - operationId: createPaymentIntent`}</code>
      </>
    ),
  },
];

const GENERIC_TOOLS = [
  [
    <code key="t">{'{name}'}_list_schemas</code>,
    'Find endpoints by category or search',
    <code key="s">{'{ category?, search? }'}</code>,
  ],
  [
    <code key="t">{'{name}'}_get_schema</code>,
    'Read one endpoint’s parameters and body, with an example call',
    <code key="s">{'{ operation }'}</code>,
  ],
  [
    <code key="t">{'{name}'}_api_call</code>,
    'Call any endpoint, with parameters routed for you',
    <code key="s">{'{ operation, params?, body? }'}</code>,
  ],
];

const TOKENS = [
  ['20 endpoints', '~5-8K', '~800'],
  ['50 endpoints', '~15-20K', '~800'],
  ['100 endpoints', '~30-40K', '~800'],
  ['300 endpoints', '~80-120K', '~800'],
];

const PARAMS = [
  {
    title: 'What the model usually has to know',
    body: (
      <code className="pg-cmd">{`path:    { petId: "123" }
query:   { status: "available" }
headers: { "X-API-Key": "sk-..." }`}</code>
    ),
  },
  {
    title: 'What a generated server asks for',
    body: (
      <code className="pg-cmd">{`operation: "getPetById"
params:    { petId: "123", status: "available" }
// Routed to path and query on the server.
// Credentials are added outside the model.`}</code>
    ),
  },
];

const SERVER = [
  {
    title: 'TypeScript on the official MCP SDK',
    body: 'Generated servers are TypeScript, run on Bun, and use the official Model Context Protocol SDK over Streamable HTTP, with per-session state, idle sessions closed after 30 minutes and a cap on open sessions.',
  },
  {
    title: 'Retries that never repeat a POST',
    body: 'Calls time out, and failed calls are retried with exponential backoff and jitter on 5xx, 429 and 408. POST and PATCH are never retried, so a flaky upstream cannot charge a customer twice.',
  },
  {
    title: 'Real-world specs',
    body: 'References are resolved and inlined, including recursive and mutually recursive schemas. Operations without an operationId get a stable name from their method and path.',
  },
  {
    title: 'Credentials stay out of the model',
    body: 'The model never sees or supplies a credential. In Generic mode, a header the model passes can never override the authentication header.',
  },
  {
    title: 'A small, non-root container',
    body: 'Each server ships with a Dockerfile on a patched Alpine base that runs as a non-root user, plus a health endpoint.',
  },
];

const PIPELINE = [
  {
    title: 'Versions, not redeploys',
    body: 'Ship a new version and Statio fetches the spec again. If the spec, the generator and your settings are unchanged it reuses the last image; if anything changed it rebuilds and tells you why.',
  },
  {
    title: 'Preview, promote, roll back',
    body: 'Each version can run on its own preview URL before you promote it. Roll back to an earlier version, cancel a build in progress or rebuild one, from the server’s page.',
  },
  {
    title: 'Private from the first deploy',
    body: 'Generated servers are never public. Only the gateway can call them, and their outbound traffic goes through the egress proxy and that server’s allowlist.',
  },
  {
    title: 'Scanned on the way in',
    body: 'When the server’s tools are discovered, their names and descriptions are scanned for prompt injection and anything suspicious is flagged for an admin.',
  },
];

const CLI = [
  [<code key="c">mcp-gen init</code>, 'Write a starter mcp-gen.yaml for a chosen mode'],
  [<code key="c">mcp-gen preview</code>, 'Show a spec’s endpoints, tags and methods, with your filters applied'],
  [<code key="c">mcp-gen scaffold</code>, 'Create a new server project with its config'],
  [
    <code key="c">mcp-gen sync</code>,
    'Run the full pipeline and write the server, or show what would change with --dry-run',
  ],
];

export default function McpGenPage() {
  return (
    <PageShell>
      <PageBody>
        <PageHero
          title="MCP servers from your OpenAPI spec"
          lede="Give Statio a spec and it writes the MCP server, builds it, hosts it privately and puts every tool behind per-tool access, without you writing a line of server code or running anything yourself."
          actions={
            <>
              <a className="s-btn s-btn-pri" href="https://app.statio.dev">
                Generate a server
              </a>
              <SmartLink className="s-btn s-btn-sec" href="/getting-started">
                Getting started
              </SmartLink>
            </>
          }
        />
        <div className="pg-film">
          <PaintingWindow art="aqueduct">
            <LiveGenerateWindow />
          </PaintingWindow>
          <p className="pg-caption">Names and numbers in the window are illustrative.</p>
        </div>

        <Section
          id="how"
          title="How it works"
          lede="Four steps in the Statio app, from a spec URL to a governed server."
          split
        >
          <Steps items={HOW} />
        </Section>

        <Section id="tokens" title="The token problem" split>
          <p>
            Most MCP servers register every tool up front. When a model connects, it receives the full catalog: every
            tool name, description and input schema, placed in its context window before the user has asked anything. A
            server with hundreds of tools can spend tens of thousands of tokens that way.
          </p>
          <p>
            That cost is why some teams give up on MCP for raw CLI wrappers, and lose access control, credential
            handling and the record of every call along the way. Generation modes are how Statio keeps MCP cheap for
            large APIs.
          </p>
        </Section>

        <Section id="modes" title="Three generation modes" split>
          <FeatureRows items={MODES} columns={1} />
        </Section>

        <Section
          id="generic-tools"
          title="The three Generic tools"
          lede="What the model sees in Generic mode, whatever the size of the API."
          split
        >
          <DataTable
            caption="The three tools Generic mode registers"
            columns={['Tool', 'Purpose', 'Input']}
            rows={GENERIC_TOOLS}
          />
          <p>An operation can be named by its operationId, by method and path, or by a concrete URL path.</p>
        </Section>

        <Section
          id="comparison"
          title="Estimated context cost"
          lede="Rough estimates of the tool list a model reads on connect, not measurements."
          split
        >
          <DataTable
            caption="Estimated tokens in the tools/list response by API size and generation mode"
            columns={['API size', 'Per Endpoint', 'Generic']}
            rows={TOKENS}
            highlight={2}
          />
        </Section>

        <Section
          id="params"
          title="One params object"
          lede="The model never has to know which parameter goes in the path, the query or a header."
          split
        >
          <FeatureRows items={PARAMS} columns={1} />
        </Section>

        <Section
          id="server"
          title="The generated server"
          lede="Production behaviour you would otherwise write by hand."
          split
        >
          <FeatureRows items={SERVER} columns={1} />
        </Section>

        <Section
          id="pipeline"
          title="Hosted on Statio"
          lede="Each server is versioned, private and governed from its first call."
          split
        >
          <FeatureRows items={PIPELINE} columns={1} />
        </Section>

        <Section
          id="cli"
          title="The mcp-gen CLI"
          lede="The same generator, driven by an mcp-gen.yaml in your repository."
          split
        >
          <DataTable caption="mcp-gen commands" columns={['Command', 'What it does']} rows={CLI} />
          <p>
            The config sets the spec (a URL or a local file), the server’s base URL and authentication, the generation
            mode and filters by tag, path, method and operation. Unknown keys are rejected, so a typo fails loudly
            instead of being ignored. Sync regenerates every file from the spec, so keep your changes in the config.
          </p>
        </Section>

        <Section id="plans" title="Plans" split>
          <Note title="Generation on every plan, images from Team">
            Generating and hosting a server from your OpenAPI spec works on every plan, including Free. If you already
            built a server, Statio can host your Docker image as it is from the Team plan up.
          </Note>
        </Section>
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}
